import { AccessTime, Close, EditOutlined } from "@mui/icons-material";
import { Alert, Box, Button, ButtonBase, Divider, Stack, styled, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { HighlightSelectBox, CanonicalKeys, MyPaper, TagsHighlightSelect, TextField, ToggleTypography, UserAvatar, UserStack } from "../../components";
import { Association, SelectBoxOption, TagInfo } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { useCreateUserAssignmentMutation, useDeleteUserAssignmentMutation, useGetTagsBasedOnParameterQuery, useGetUsersAsOptionsQuery, useLazyGetUserAssignmentsQuery, User, UserAssignment } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { openDialog } from "../../redux/slices/dialog";
import { AssignHistory } from "./assignHistory";

const CustomButton = styled(ButtonBase)(({ theme }) => ({
    fontSize: 13,
    width: '100%',
    textAlign: 'left',
    paddingTop: '4px',
    paddingBottom: '2px',
    fontWeight: 500,
    borderTopRightRadius: '4px',
    borderTopLeftRadius: '4px',
    fontFamily: 'htrts_medium',
    '&:hover,&:focus': {
        paddingLeft: '4px',
        paddingRight: '4px',
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
    },
    '& span': {
        width: '100%',
    },
    '& svg': {
        width: 16,
        height: 16,
    },
}));

export const UserAssignmentSection: React.FC<{
    objectId: string, objectType: Association['object_type'],
    users: string[], onUpdated?: () => void,
    workflow_canonical_name: CanonicalKeys;
}> = ({ objectId, objectType, users, onUpdated, workflow_canonical_name }) => {

    const dispatch = useAppDispatch();

    const usersOptions = useGetUsersAsOptionsQuery(null);
    const wfAssignmentPurposeData = useGetTagsBasedOnParameterQuery(workflow_canonical_name);

    const [getUserAssignment, userAssignmentData] = useLazyGetUserAssignmentsQuery();

    const [assignUser] = useCreateUserAssignmentMutation();
    const [unassignUser] = useDeleteUserAssignmentMutation();

    const [userAssignment, setUserAssignment] = React.useState<UserAssignment>();
    const [edit, setEdit] = React.useState<boolean>(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setEdit(!edit);
    };

    const handleClose = () => {
        setEdit(false);
        setUserAssignment(userAssignmentData.data?.data?.[0]);
    };

    const openAssignHistory = (id?: string) => {
        if (userAssignment?._id || id) {
            dispatch(openDialog({
                title: `Assign History`,
                hidePositiveBtn: true,
                hideNegativeBtn: true,
                body: <AssignHistory user_assignment_id={id || userAssignment?._id || ''} />
            }));
        } else {
            fetchUserAssignmentData(true);
        }
    };

    const updateUserAssignmentDetails = (_userAssignment: UserAssignment) => {
        if (wfAssignmentPurposeData.isSuccess && userAssignmentData.isSuccess && _userAssignment && _userAssignment?.assigned_user_id) {
            assignUser({
                assigned_user_id: _userAssignment?.assigned_user_id,
                notes: _userAssignment?.notes ?? "",
                object_id: objectId,
                object_type: objectType,
                followers: _userAssignment?.followers,
                workflow_tag: _userAssignment.workflow_tag ? {
                    tag_group_id: _userAssignment.workflow_tag?.tag_group_id,
                    tag_id: _userAssignment.workflow_tag?._id ?? _userAssignment.workflow_tag?.tag_id
                } : {
                    tag_group_id: wfAssignmentPurposeData?.data?.tags?.[0]?.tag_group_id,
                    tag_id: wfAssignmentPurposeData?.data?.tags?.[0]?._id
                }
            }).then(res => {
                onUpdated?.();
            });
        } else if (!_userAssignment?.assigned_user_id) {
            if (userAssignment?._id) {
                unassignUser(userAssignment?._id).then(res => {
                    onUpdated?.();
                });
            }
        }
        setEdit(false);
    };

    const updateUserAssignmentState = (_userAssignment: Partial<UserAssignment>, hitApi: boolean) => {
        let newUserAssignment = {
            ...userAssignment,
            _id: userAssignment?._id ?? "",
            assigned_user_id: userAssignment?.assigned_user_id ?? "",
            assigned_user_name: userAssignment?.assigned_user_name,
            notes: userAssignment?.notes ?? "",
            object_id: userAssignment?.object_id ?? "",
            object_type: userAssignment?.object_type ?? "task",
            ..._userAssignment
        };

        setUserAssignment({ ...newUserAssignment });

        if (hitApi) {
            updateUserAssignmentDetails(newUserAssignment);
        }
    };

    const fetchUserAssignmentData = async (isOpenAssignHistory?: boolean) => {
        if (isOpenAssignHistory) {
            dispatch(openBackdrop('Fetching user assignment data...'));
        }
        const _userAssignment = await getUserAssignment({ object_id: objectId, object_type: objectType, page_no: 1, page_size: 1 }).unwrap();
        setUserAssignment(_userAssignment?.data?.[0]);
        if (isOpenAssignHistory) {
            dispatch(closeBackdrop());
            openAssignHistory(_userAssignment?.data?.[0]?._id);
        }
    };

    React.useEffect(() => {
        fetchUserAssignmentData();
        // eslint-disable-next-line
    }, [objectId, objectType]);

    let filteredUserOption: SelectBoxOption[] = usersOptions.data?.filter(user => users.includes(`${user.value}`)) ?? [];

    if (users.length === 0) {
        filteredUserOption = usersOptions.data ?? [];
    };

    return <MyPaper>
        <Stack gap={1}>
            <Box>
                <CustomButton disableRipple onClick={(e) => !edit ? handleClick(e) : handleClose()}>
                    <span>{"User Assignment"}</span>
                    {!edit && <EditOutlined fontSize="small" />}
                    {edit && <Close fontSize="small" />}
                </CustomButton>

                <Divider sx={{ mt: '1px' }} />

                {userAssignment?.assigned_user_id && !edit && <Stack direction={"row"} gap={1} sx={{ mt: 1, alignItems: "center" }}>

                    <UserAvatar userId={userAssignment?.assigned_user_id} name={`${userAssignment?.assigned_user_name ?? ""}`} avatarUrl={""} sx={{
                        width: 26,
                        height: 26,
                        fontSize: 10
                    }} variant="rounded" />

                    <Box>
                        <Typography variant="body2" sx={{ fontSize: 12 }}>{userAssignment.assigned_user_name}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: "block", fontSize: 10 }}>{userAssignment?.workflow_tag?.tag_name ?? ""}</Typography>
                    </Box>
                </Stack>}

                {!(userAssignment?.assigned_user_id) && !edit && <Typography variant="caption">None</Typography>}
            </Box>

            {userAssignment?.assigned_user_id && !edit && <>
                {/* Notes */}
                <Box>
                    <Typography variant="body2" sx={{ fontSize: 12 }}>Notes:</Typography>
                    <ToggleTypography color="textSecondary" sx={{ fontSize: 10.5 }} content={userAssignment?.notes ?? "-"} />
                </Box>

                {/* Followers */}
                <UserStack
                    title="Followers"
                    buttonName="Followers"
                    user={userAssignment?.followers ?? []}
                    updatedUsers={(users: User[]) => {
                        let newUserAssignment: Partial<UserAssignment> = {
                            followers: users?.map(user => user.user_id)
                        };
                        updateUserAssignmentState(newUserAssignment, true);
                    }}
                    onlyUsers={users}
                    avatarSize="small"
                />

                {/* View Assign History */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        size="small"
                        variant="text"
                        sx={{
                            width: "fit-content",
                            textTransform: "none",
                            fontSize: 12,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                        }}
                        onClick={() => openAssignHistory()}
                    >
                        <div>View Assign History</div>
                        <AccessTime fontSize="small" sx={{ fontSize: 16 }} color="primary" />
                    </Button>
                </Box>
            </>
            }

            {
                edit && <Stack gap={2} mt={1}>
                    {/* Assigned To */}
                    <HighlightSelectBox
                        required
                        margin="none"
                        size="small"
                        fullWdith
                        options={filteredUserOption}
                        label={'Assign To'}
                        value={userAssignment?.assigned_user_id ? {
                            value: `${userAssignment.assigned_user_id}`,
                            label: `${userAssignment.assigned_user_name ?? "-"}`
                        } : null}
                        onChange={(option: SelectBoxOption | null) => {
                            if (option) {
                                let newUserAssignment: Partial<UserAssignment> = {
                                    assigned_user_id: `${option?.value ?? ""}`,
                                    assigned_user_name: option?.label ?? ""
                                };
                                updateUserAssignmentState(newUserAssignment, false);
                            } else {
                                let newUserAssignment: Partial<UserAssignment> = {
                                    assigned_user_id: "",
                                    assigned_user_name: ""
                                };
                                updateUserAssignmentState(newUserAssignment, true);
                            }
                        }}
                        loading={usersOptions.isLoading || usersOptions.isFetching}
                        error={usersOptions.isError}
                        helperText={usersOptions.isError ? "Something went wrong, Unable to fetch users." : ""}
                    />

                    {/* Workflow */}
                    <TagsHighlightSelect
                        id="task-workflow"
                        required
                        margin="none"
                        size="small"
                        fullWdith
                        value={(userAssignment && userAssignment.workflow_tag) ? {
                            ...userAssignment?.workflow_tag,
                            label: `${userAssignment?.workflow_tag?.tag_name}`,
                            value: `${userAssignment?.workflow_tag?.tag_id}`
                        } : null}
                        onChange={(option: SelectBoxOption) => {
                            let newUserAssignment: Partial<UserAssignment> = {
                                workflow_tag: option as unknown as TagInfo
                            };
                            updateUserAssignmentState(newUserAssignment, false);
                        }}
                        canonical_name={workflow_canonical_name}
                    />

                    {/* Notes */}
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        multiline
                        rows={5}
                        label={`Notes (${userAssignment?.notes?.length ?? 0}/500)`}
                        placeholder={"Please enter notes"}
                        onChange={(e) => {
                            if (e.target.value?.length <= 500) {
                                updateUserAssignmentState({ notes: e.target.value }, false);
                            }
                        }}
                        value={userAssignment?.notes ?? ''}
                    />

                    {/* Followers */}
                    <UserStack
                        title="Followers"
                        buttonName="Followers"
                        user={userAssignment?.followers ?? []}
                        updatedUsers={(users: User[]) => {
                            let newUserAssignment: Partial<UserAssignment> = {
                                followers: users?.map(user => user.user_id)
                            };
                            updateUserAssignmentState(newUserAssignment, false);
                        }}
                        onlyUsers={users}
                        avatarSize="small"
                    />

                    {/* Alert Message */}
                    {!(userAssignment?.assigned_user_id && userAssignment?.workflow_tag) && <Alert severity="warning">Please select user and workflow tag to assign user.</Alert>}

                    {/* Save Button */}
                    <Button
                        disabled={!userAssignment?.assigned_user_id}
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            if (userAssignment?.assigned_user_id && userAssignment?.workflow_tag) {
                                updateUserAssignmentDetails(userAssignment)
                            } else {
                                enqueueSnackbar("Please select user and workflow tag.", { variant: 'error' });
                            }
                        }}>
                        Save
                    </Button>
                </Stack>
            }
        </Stack >
    </MyPaper >
}